/**
 * Milestone & Achievement Sequence Templates
 * Based on best practices from Fitbit, Strava, LinkedIn, Duolingo
 *
 * Key insights from billion-dollar companies:
 * - Celebrate wins = increased engagement + sharing
 * - Gamification drives 30%+ higher retention
 * - Milestones are perfect moments for upsell
 * - Social sharing at milestones = free marketing
 */

import { SequenceTemplate } from "./types";

export const FIRST_GENERATION_CELEBRATION: SequenceTemplate = {
  id: "first-generation-celebration",
  name: "First AI Generation Celebration",
  type: "milestone",
  description: "Celebrate and guide users after their first AI copilot generation",
  strategy: "Celebrate the win, then guide to next steps. This is the highest-intent moment - strike while the iron is hot.",
  icon: "TrophyOutlined",
  color: "#f59e0b",
  expectedOutcomes: [
    "50%+ engagement with next steps",
    "Increased time-to-value",
    "Higher activation rate",
    "Social sharing opportunity"
  ],
  bestPractices: [
    "Send immediately after first generation",
    "Celebrate their achievement genuinely",
    "Provide clear next step (explore more copilots)",
    "Include shareable achievement badge",
    "Set expectations for what happens next"
  ],
  trigger: {
    eventType: "firstGeneration",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active", "trialing"]
    }
  },
  emails: [
    {
      name: "Immediate - Congratulations!",
      subject: "You did it! Your first AI generation is complete",
      suggestedTemplateId: "product-launch",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Celebrate + immediate next step (explore copilots)",
      variables: [
        { key: "copilot_name", defaultValue: "{{copilot_name}}" },
        { key: "next_step", defaultValue: "Try more AI copilots" },
        { key: "copilots_url", defaultValue: "{{copilots_url}}" }
      ]
    },
    {
      name: "Day 2 - Pro Tips",
      subject: "3 ways to get even more from AI copilots",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Help them maximize copilot usage"
    }
  ],
  tags: ["milestone", "first-generation", "celebration", "activation"],
  bestFor: ["AI platforms", "Creator tools", "Productivity apps"],
  difficulty: "beginner",
  setupTime: "10 minutes"
};

export const GENERATIONS_MILESTONE: SequenceTemplate = {
  id: "generations-100",
  name: "100 Generations Milestone",
  type: "milestone",
  description: "Celebrate when a user hits 100 AI generations",
  strategy: "Social proof moment - show ROI and encourage upgrade/share. Users who hit this milestone have 80%+ retention.",
  icon: "MessageOutlined",
  color: "#10b981",
  expectedOutcomes: [
    "High engagement (celebrating success)",
    "Upgrade opportunity",
    "Social sharing/testimonial",
    "Increased product stickiness"
  ],
  bestPractices: [
    "Show impact: 100 generations = X hours saved",
    "Include stats: most used copilots, credits saved",
    "Ask for testimonial/review (they're happy!)",
    "Suggest next milestone goal"
  ],
  trigger: {
    eventType: "timeAfterSignup", // Would be generation_milestone in real implementation
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Milestone Reached!",
      subject: "{{first_name}}, you hit 100 AI generations!",
      suggestedTemplateId: "monthly-analytics",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Celebrate + show impact/ROI",
      variables: [
        { key: "generations_count", defaultValue: "100" },
        { key: "hours_saved", defaultValue: "{{hours_saved}}" },
        { key: "top_copilot", defaultValue: "{{top_copilot}}" }
      ]
    },
    {
      name: "Day 2 - Next Level",
      subject: "Ready for the next level? Here's how",
      suggestedTemplateId: "product-upsell",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Upsell opportunity + advanced AI models"
    }
  ],
  tags: ["milestone", "generations", "celebration", "upsell"],
  bestFor: ["AI platforms", "Productivity tools", "Usage-based products"],
  difficulty: "beginner",
  setupTime: "10 minutes"
};

export const ANNIVERSARY: SequenceTemplate = {
  id: "anniversary-1-year",
  name: "1-Year Anniversary",
  type: "milestone",
  description: "Celebrate customer's 1-year anniversary",
  strategy: "Retention gold - customers who celebrate anniversaries have 90%+ renewal rates. Make them feel special.",
  icon: "GiftOutlined",
  color: "#8b5cf6",
  expectedOutcomes: [
    "Strong emotional connection",
    "High renewal/upsell potential",
    "Testimonial opportunity",
    "Social sharing"
  ],
  bestPractices: [
    "Personalize with their journey stats",
    "Offer exclusive anniversary perk",
    "Include CEO/founder message",
    "Make them feel like family",
    "Ask for review/referral"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 365, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Happy Anniversary!",
      subject: "Happy 1-year anniversary, {{first_name}}!",
      suggestedTemplateId: "product-launch",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Celebrate + exclusive anniversary offer",
      variables: [
        { key: "year_stats", defaultValue: "{{total_generations}}" },
        { key: "anniversary_perk", defaultValue: "1 month free" },
        { key: "founder_name", defaultValue: "The Copilotly Team" }
      ]
    },
    {
      name: "Day 3 - Your Year in Review",
      subject: "Your year with {{product_name}}: The highlights",
      suggestedTemplateId: "performance-report",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Year-in-review + impact stats"
    }
  ],
  tags: ["milestone", "anniversary", "celebration", "loyalty"],
  bestFor: ["Subscription products", "Long-term relationships", "B2B SaaS"],
  difficulty: "beginner",
  setupTime: "15 minutes"
};

export const CREDITS_LIMIT_WARNING: SequenceTemplate = {
  id: "credits-limit-warning",
  name: "Credits Limit Approaching",
  type: "milestone",
  description: "Alert users when approaching plan credit limits",
  strategy: "Proactive communication prevents frustration. Position upgrade as enablement, not upsell. 60%+ conversion when done right.",
  icon: "WarningOutlined",
  color: "#f97316",
  expectedOutcomes: [
    "Prevent service interruption",
    "60%+ upgrade conversion",
    "Positive customer experience",
    "Trust building"
  ],
  bestPractices: [
    "Alert at 80%, not 100%",
    "Show exactly what they're using",
    "Make upgrade 1-click easy",
    "Offer temporary grace period",
    "Position as helping, not selling"
  ],
  trigger: {
    eventType: "creditsLow",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: ["Free", "Optimizer"],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "80% Credits Used Alert",
      subject: "Heads up: You've used 80% of your credits",
      suggestedTemplateId: "usage-report",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Friendly heads up + upgrade option",
      variables: [
        { key: "usage_percent", defaultValue: "80%" },
        { key: "credits_remaining", defaultValue: "{{credits_remaining}}" },
        { key: "upgrade_benefit", defaultValue: "Unlimited AI generations" }
      ]
    },
    {
      name: "95% Credits Used Alert",
      subject: "Almost at your limit - upgrade to keep going",
      suggestedTemplateId: "usage-report",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Urgency + clear upgrade path"
    },
    {
      name: "Credits Exhausted",
      subject: "You've used all your credits",
      suggestedTemplateId: "trial-expiring",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Clear next steps to continue"
    }
  ],
  tags: ["milestone", "credits", "limit", "upgrade", "upsell"],
  bestFor: ["Credit-based pricing", "Freemium products", "Tiered plans"],
  difficulty: "intermediate",
  setupTime: "15 minutes"
};

export const MILESTONE_TEMPLATES = [
  FIRST_GENERATION_CELEBRATION,
  GENERATIONS_MILESTONE,
  ANNIVERSARY,
  CREDITS_LIMIT_WARNING
];
